import * as React from 'react';
import {
  Logging,
  API,
  ResponseError,
  NotificationData,
  Certificate,
  CustomerSummaryCollection,
  CustomerSummary,
  getUuidV4,
} from '@vcd-ecm-ui/Common';
import { useDispatch } from 'react-redux';

const useCertificate = (
  gatewayEndPoint: string,
  certificateUuid: string,
  certificateId: number,
  isSideBySideViewerOpen: boolean,
  key: string, // If the 'key' changes the Certificate will reload
): [Certificate, CustomerSummary[], NotificationData, string] => {
  const [certificate, setCertificate] = React.useState(null);
  const [childCustomers, setChildCustomers] = React.useState(null);
  const [certificateNotification, setCertificateNotification] = React.useState(null);
  const [certificateKey, setCertificateKey] = React.useState(getUuidV4());

  const dispatch = useDispatch();

  const getChildCustomers = (parentCustomerUuid: string, parentCustomerName: string) => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      // Load the Child Customers if this is a Parent Customer
      if (parentCustomerUuid) {
        const maxPageSize = 1000;
        let customFilter: string = null;

        const filterValue = `(parentUuid eq '${parentCustomerUuid}')`;
        customFilter = `$filter=${encodeURIComponent(filterValue)}`;

        // Get the Child Customers for the Parent Customer
        const collection: CustomerSummaryCollection = await api.customer.getCustomersV41(0, maxPageSize, null, null, customFilter);

        Logging.Info('Certificate Child Customers:', parentCustomerUuid, parentCustomerName, collection.value);

        // Set the Child Customers
        setChildCustomers(collection.value);

        // Set the Unique Key associated with this Certificate.  Changes if the Certificate is reloaded after an Update
        setCertificateKey(getUuidV4());
      }
    } catch (error) {
      const notification: NotificationData = {
        type: 'Error',
        title: 'Child Customers Read Error',
        description: `Unable to read Child Customers '${parentCustomerName}'.`,
        error: error as ResponseError,
      };
      setCertificateNotification(notification);
    }
  };

  const getCertificate = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);

      const certificateApi: Certificate = await api.certificate.getCertificateV41(certificateUuid);

      Logging.Info('Certificate:', certificateUuid, certificateId, certificateApi);
      setCertificate(certificateApi);

      // Check if the Customer associated with the Certificate is a Parent
      const isParentCustomer = certificateApi.customer?.isParentInd;

      // Load the Child Customers if this is a Parent Customer
      if (isParentCustomer) {
        dispatch(getChildCustomers(certificateApi.customer?.uuid, certificateApi.customer?.name));
      } else {
        // Set the Unique Key associated with this Certificate.  Changes if the Certificate is reloaded after an Update
        setCertificateKey(getUuidV4());
      }
    } catch (error) {
      const notification: NotificationData = {
        type: 'Error',
        title: 'Certificate Read Error',
        description: `Unable to read Certificate ${certificateId}.`,
        error: error as ResponseError,
      };
      setCertificateNotification(notification);
    }
  };

  React.useEffect(() => {
    if (isSideBySideViewerOpen) {
      // Side By Side Viewer is Open - Get the Certificate
      dispatch(getCertificate());
    } else {
      // Side By Side Viewer is Closed - Clear the Certificate
      setCertificate(null);
      setChildCustomers(null);
      setCertificateNotification(null);
    }
  }, [certificateId, certificateUuid, isSideBySideViewerOpen, key]);

  return [certificate, childCustomers, certificateNotification, certificateKey];
};

export default useCertificate;
